import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { exportToExcel } from '../../../Export/ReportExcel';
import { exportToPDF } from '../../../Export/ReportPDF';
import { Otbreportpayload } from '../../../Payload/OTBReportPayload';
import { SaveOTBMemorize, ResetOTBMemorize, getPrivileges } from '../../../API/nimbleAPI';
import { COLORS } from '../../../Components/colors';

const COLUMN_KEYS = [
  "Date", "DOW", "CorporationName", "BrandName",
  "TotalRooms", "TrnsRooms", "GrpRooms",
  "OTBRoomsSold", "OTBOcc", "OTBADR", "OTBRoomRev", "OTBRevPar",
  "PickUpRooms", "PickUpADR", "PickUpRev", "PickUpOcc",
  "BudgetRoomsSold", "BudgetOcc", "BudgetAdr", "BudgetRoomRev", "BudgetRevPar",
  "RoomsSoldVariance", "ADRVariance", "RoomRevenueVariance", "OccVariance",
  "LYRoomsSold", "LYOccupancy", "LYADR", "LYRoomRev", "LYRevPar",
  "LYRoomsSoldVariance", "LYADRVariance", "LYRoomRevenueVariance", "LYOccVariance"
];

const PickupDurationEnum = {
  "n/a": 0,
  "1day": 1,
  "3days": 2,
  "7days": 3,
  "14days": 4,
  "30days": 5
};

const ViewTransactionEnum = {
  "next7days": 1,
  "next14days": 2,
  "next30days": 3,
  "next45days": 4,
  "next60days": 5,
  "next90days(nextquarter)": 6,
  "next120days": 7,
  "next6months": 8,
  "nextfinancialquarter": 9,
  "next9months": 10,
  "next12months(1yearforecast)": 11,
  "customdaterange": 12
};

const Buttons = ({ filters, dateRange, reportPayload, displaySettings, data, metaData, onRefresh, showLegalName, totals }) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [privileges, setPrivileges] = useState(null);
  const [permissionSnackbarOpen, setPermissionSnackbarOpen] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState("");

  // Dialog States
  const [saveReportOpen, setSaveReportOpen] = useState(false);
  const [reportName, setReportName] = useState('');

  const notifyNoPermission = useCallback((type = "update") => {
    const msg = type === "create"
      ? "You don't have create permission."
      : "You don't have update permission.";
    setPermissionMessage(msg);
    setPermissionSnackbarOpen(true);
  }, []);

  const handleCustomize = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const corporationID = searchParams.get('corporationID');
    const pcID = searchParams.get('PCID') || searchParams.get('pcid') || searchParams.get('PCIdLst') || searchParams.get('PCIdlist') || reportPayload?.PCIdlist || reportPayload?.PCIdLst;
    const authID = searchParams.get('authID');
    const userID = searchParams.get('userId') || searchParams.get('userID');
    const menuID = searchParams.get('menuId') || searchParams.get('menuID');
    const urlInfo = searchParams.get('urlInfo')

    const query = new URLSearchParams();

    if (corporationID) query.set('corporationID', corporationID);
    if (pcID) query.set('PCID', pcID);
    if (authID) query.set('authID', authID);
    if (userID) query.set('userId', userID);
    if (menuID) query.set('menuId', menuID);
    if (urlInfo) query.set('urlInfo', urlInfo);


    navigate(`/OTB?${query.toString()}`, { state: { filters, dateRange, reportPayload, displaySettings } });
  }, [navigate, filters, dateRange, reportPayload, displaySettings, privileges, notifyNoPermission]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.altKey && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        handleCustomize();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCustomize]);

  useEffect(() => {
    const fetchPrivileges = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const userID = searchParams.get('userId') || searchParams.get('userID');
      const menuID = searchParams.get('menuId') || searchParams.get('menuID');

      if (!userID || !menuID) return;

      try {
        const res = await getPrivileges({ UserID: userID, MenuIds: menuID });
        const details = res?.Details?.[0];
        if (details) {
          setPrivileges({
            create: Boolean(details.Create),
            update: Boolean(details.Update),
            view: Boolean(details.View),
            delete: Boolean(details.Delete)
          });
        }
      } catch (error) {
        console.error("Error fetching privileges:", error);
      }
    };

    fetchPrivileges();
  }, []);

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh(metaData);
    } else {
      window.location.reload();
    }
  };

  const handleExportChange = (event) => {
    if (privileges && !privileges.create) {
      notifyNoPermission("create");
      return;
    }
    const val = event.target.value;
    setExportTo(''); // Reset to allow re-selection
    if (val === 'Excel') {
      exportToExcel(data, reportPayload, filters?.pickupDuration, filters?.selectedRows, displaySettings, dateRange, showLegalName, totals);
    } else if (val === 'PDF') {
      exportToPDF(data, reportPayload, filters?.pickupDuration, filters?.selectedRows, dateRange, displaySettings, showLegalName, totals);
    }
  };

  const handleSettingsChange = async (event) => {
    const val = event.target.value;
    setSettings(''); // Reset
    if (val === 'Memorize') {
      const searchParams = new URLSearchParams(window.location.search);
      const userId = searchParams.get('userId') || searchParams.get('userID');
      const menuId = searchParams.get('menuId') || searchParams.get('menuID');

      let viewTransType = 0;
      if (filters?.viewTransactionRange) {
        const normalizedViewTrans = String(filters.viewTransactionRange).replace(/\s+/g, '').toLowerCase();
        if (ViewTransactionEnum[normalizedViewTrans] !== undefined) {
          viewTransType = ViewTransactionEnum[normalizedViewTrans];
        }
      }

      let customizeString = '';
      COLUMN_KEYS.forEach(key => {
        if (reportPayload?.[key]) {
          customizeString += `%${key}`;
        }
      });

      if (viewTransType === 12) {
        if (reportPayload?.ShortFromDate) customizeString += `%ShortFromDate:${reportPayload.ShortFromDate}`;
        if (reportPayload?.ShortToDate) customizeString += `%ShortToDate:${reportPayload.ShortToDate}`;
      }

      if (customizeString) customizeString += '%';

      let pickupValue = reportPayload?.PickUpDays || 0;
      if (filters?.pickupDuration) {
        const normalizedLabel = String(filters.pickupDuration).replace(/\s+/g, '').toLowerCase();
        if (PickupDurationEnum[normalizedLabel] !== undefined) {
          pickupValue = PickupDurationEnum[normalizedLabel];
        }
      }

      const payload = {
        UserId: userId,
        MenuId: menuId,
        ViewTransType: viewTransType,
        PickUpdays: pickupValue,
        ReportTitle: "otb_report",
        ShowNegFig: displaySettings?.negativeFormat === 'parentheses' ? 1 : (displaySettings?.negativeFormat === 'trailing' ? 2 : 0),
        InRed: displaySettings?.showRedNegative || false,
        WOCents: displaySettings?.withoutCents || false,
        ExcludeZero: displaySettings?.excludeZero || false,
        CustomizeString: customizeString,
        DisplayTextFormat: displaySettings?.textFormat === 'upper' ? 2 : (displaySettings?.textFormat === 'lower' ? 1 : 0)
      };

      await SaveOTBMemorize(payload);
    } else if (val === 'Reset') {
      const searchParams = new URLSearchParams(window.location.search);
      const userId = searchParams.get('userId') || searchParams.get('userID') || '';
      const menuId = searchParams.get('menuId') || searchParams.get('menuID') || '';

      await ResetOTBMemorize({
        UserId: userId,
        MenuId: menuId
      });
    }
  };

  const dropdownStyle = {
    minWidth: 85,
    bgcolor: COLORS.bgWhite,
    '& .MuiInputBase-root': {
      height: '28px',
      fontSize: '12px'
    },
    '& .MuiSelect-select': {
      py: 0,
      px: 1,
      display: 'flex',
      alignItems: 'center'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${COLORS.border}`
    }
  };

  const menuProps = {
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root': {
          fontSize: '12px',
        },
      },
    },
  };

  const buttonStyle = {
    bgcolor: COLORS.primary,
    color: COLORS.textWhite,
    textTransform: 'none',
    border: `1px solid ${COLORS.border}`,
    borderRadius: 1,
    fontWeight: 400,
    px: 1.25,
    height: '28px',
    fontSize: '12px',
    minWidth: '85px',
    fontFamily: "Poppins"
  };



  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end', // 👈 push everything right
        alignItems: 'center',
        p: 0.5,
        bgcolor: COLORS.bgLighter,
        borderBottom: `1px solid ${COLORS.borderLight}`
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Button sx={buttonStyle} onClick={handleRefresh}>Refresh</Button>

        <Tooltip arrow>
          <span>
            <FormControl size="small" sx={dropdownStyle}>
              <Select
                value={settings}
                // onChange={handleSettingsChange}
                onChange={handleSettingsChange}
                displayEmpty
                IconComponent={ExpandMoreIcon}
                renderValue={(selected) => selected || 'Settings'}
                MenuProps={menuProps}
                sx={{ background: COLORS.primary, color: COLORS.textWhite }}
              >
                <MenuItem value="Memorize">Memorize</MenuItem>
                <MenuItem value="Reset">Reset</MenuItem>
              </Select>
            </FormControl>
          </span>
        </Tooltip>

        <Tooltip arrow>
          <span>
            <FormControl size="small" sx={dropdownStyle}>
              <Select
                value={exportTo}
                onChange={handleExportChange}
                displayEmpty
                IconComponent={ExpandMoreIcon}
                renderValue={(selected) => selected || 'Export to'}
                MenuProps={menuProps}
                sx={{ background: COLORS.primary, color: COLORS.textWhite }}
              >
                <MenuItem value="PDF">PDF</MenuItem>
                <MenuItem value="Excel">Excel</MenuItem>
              </Select>
            </FormControl>
          </span>
        </Tooltip>

        <Tooltip arrow>
          <span>
            <Button
              sx={buttonStyle}
              onClick={handleCustomize}
            >
              Customize
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* Save Report Dialog */}
      <Dialog open={saveReportOpen} onClose={() => setSaveReportOpen(false)}>
        <DialogTitle>Save Custom Report</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter a name for your custom report.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Report Name"
            fullWidth
            variant="standard"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveReportOpen(false)}>Cancel</Button>
          <Button onClick={() => { console.log("Saved:", reportName); setSaveReportOpen(false); }}>Save</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={permissionSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setPermissionSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setPermissionSnackbarOpen(false)} severity="error" variant="filled" sx={{ width: "100%", fontSize: '12px' }}>
          {permissionMessage}
        </Alert>
      </Snackbar>



    </Box>
  );
};

export default Buttons;
